// ============================================================================
// CHANGO V5.0 — D3 Product Detail / Create (Task #64)
// ============================================================================
// Mount:
//   /app/products/new  → create form
//   /app/products/:id  → view / edit / archive / restore
//
// §20 red-line guards at the UI layer:
//   #17 sales never sees the default_cost_rmb field (backend strips it too).
//   #19 no hardcoded prices — everything comes from user input.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import { hasWorkspaceLayer } from '../../utils/layerPermissions.js';

const CURRENCIES = ['CNY', 'USD', 'EUR', 'JPY', 'GBP'];
const SKU_RE = /^[A-Za-z0-9][A-Za-z0-9._\-/]{0,39}$/;

const BLANK = {
  sku_code: '',
  name: '',
  name_en: '',
  description: '',
  description_en: '',
  unit: '',
  default_price_rmb: '',
  default_cost_rmb: '',
  currency: 'CNY',
  tags: [],
};

function parsePrice(v) {
  if (v === '' || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export default function ProductDetail() {
  const { id } = useParams();
  const nav    = useNavigate();
  const { user, workspace } = useAuth() || {};
  const isNew   = id === undefined || id === 'new';
  const isSales = user?.role === 'sales';
  const canArchive = user?.role === 'boss' || user?.role === 'finance';
  const hasFactoryLayer = hasWorkspaceLayer(workspace, 'A');

  const [form, setForm] = useState(BLANK);
  const [row, setRow]   = useState(null);         // full DB row (archived_at, etc.)
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const reload = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/products/${encodeURIComponent(id)}`);
      setRow(res);
      setForm({
        sku_code: res.sku_code || '',
        name: res.name || '',
        name_en: res.name_en || '',
        description: res.description || '',
        description_en: res.description_en || '',
        unit: res.unit || '',
        default_price_rmb: res.default_price_rmb ?? '',
        default_cost_rmb: res.default_cost_rmb ?? '',
        currency: res.currency || 'CNY',
        tags: Array.isArray(res.tags) ? res.tags : [],
      });
    } catch (err) {
      setError(err?.message || '加载产品失败');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { reload(); }, [reload]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const errs = {};
    if (!form.sku_code || !SKU_RE.test(form.sku_code.trim())) {
      errs.sku_code = '1–40 字符，字母数字开头，只允许 . _ - /';
    }
    if (!form.name || form.name.trim().length === 0) {
      errs.name = '必填';
    }
    if (form.name && form.name.length > 200) {
      errs.name = '最长 200 字符';
    }
    if (form.description && form.description.length > 1000) {
      errs.description = '最长 1000 字符';
    }
    const p = Number(form.default_price_rmb);
    if (form.default_price_rmb !== '' && (!Number.isFinite(p) || p < 0)) {
      errs.default_price_rmb = '必须是非负数字';
    }
    if (!isSales && form.default_cost_rmb !== '') {
      const c = Number(form.default_cost_rmb);
      if (!Number.isFinite(c) || c < 0) errs.default_cost_rmb = '必须是非负数字';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        sku_code: form.sku_code.trim(),
        name: form.name.trim(),
        name_en: form.name_en?.trim() || null,
        description: form.description?.trim() || null,
        description_en: form.description_en?.trim() || null,
        unit: form.unit?.trim() || null,
        default_price_rmb: parsePrice(form.default_price_rmb),
        currency: form.currency || 'CNY',
        tags: form.tags,
      };
      if (!isSales) {
        payload.default_cost_rmb = parsePrice(form.default_cost_rmb);
      }
      if (isNew) {
        const res = await api.post('/api/products', payload);
        nav(`/app/products/${res.id}`, { replace: true });
      } else {
        await api.patch(`/api/products/${encodeURIComponent(id)}`, payload);
        await reload();
      }
    } catch (err) {
      if (err instanceof HttpError && err.code === 'SKU_CODE_TAKEN') {
        setFieldErrors((fe) => ({ ...fe, sku_code: '该 SKU 已存在' }));
      } else {
        setError(err?.message || '保存失败');
      }
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm('确认归档该产品？可从"已归档"列表中恢复。')) return;
    try {
      await api.post(`/api/products/${encodeURIComponent(id)}/archive`, {});
      await reload();
    } catch (err) {
      setError(err?.message || '归档失败');
    }
  };

  const restore = async () => {
    try {
      await api.post(`/api/products/${encodeURIComponent(id)}/restore`, {});
      await reload();
    } catch (err) {
      setError(err?.message || '恢复失败');
    }
  };

  if (loading) {
    return <div className="px-6 py-6 text-slate-400">加载中…</div>;
  }

  return (
    <div className="px-6 py-6 max-w-3xl mx-auto">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <Link to="/app/products" className="text-xs text-slate-500 hover:text-chango-700">← 返回产品列表</Link>
          <h1 className="mt-1 text-xl font-semibold text-slate-800">
            {isNew ? '新建产品' : (row?.name || form.name || '产品详情')}
          </h1>
          {row?.archived_at && (
            <span className="mt-1 inline-flex items-center rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              已归档 · {row.archived_at}
            </span>
          )}
        </div>
        {!isNew && canArchive && (
          <div className="flex items-center gap-2">
            {row?.archived_at
              ? <button type="button" className="btn-secondary" onClick={restore}>恢复</button>
              : <button type="button" className="btn-secondary" onClick={archive}>归档</button>}
          </div>
        )}
      </header>

      {error && (
        <div className="mb-4 rounded-md border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-800">
          {error}
        </div>
      )}

      {/* --- Factory Management Links (A layer + boss/finance only) --- */}
      {!isNew && !isSales && hasFactoryLayer && row && (
        <div className="card mb-4 p-4 bg-blue-50 border border-blue-200">
          <p className="text-xs text-slate-600 mb-2">工厂管理</p>
          <div className="flex gap-2">
            <Link
              to={`/app/factory/bom-items/${encodeURIComponent(row.sku_code)}`}
              className="btn-secondary text-sm"
            >
              🏗️ BOM 编辑
            </Link>
            <Link
              to={`/app/products/${encodeURIComponent(row.sku_code)}/cost-breakdown`}
              className="btn-secondary text-sm"
            >
              📊 成本核算
            </Link>
            <Link
              to={`/app/products/${encodeURIComponent(row.sku_code)}/sensitivity`}
              className="btn-secondary text-sm"
            >
              🌪️ 敏感性分析
            </Link>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="card p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm text-slate-600">SKU 编码 *</span>
            <input
              type="text"
              className="input-text mt-1 w-full font-mono"
              value={form.sku_code}
              onChange={(e) => setField('sku_code', e.target.value)}
              placeholder="CHG-001"
              required
            />
            {fieldErrors.sku_code && <p className="text-xs text-rose-600 mt-1">{fieldErrors.sku_code}</p>}
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">中文名称 *</span>
            <input
              type="text"
              className="input-text mt-1 w-full"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              required
            />
            {fieldErrors.name && <p className="text-xs text-rose-600 mt-1">{fieldErrors.name}</p>}
          </label>
        </div>

        <label className="block">
          <span className="text-sm text-slate-600">英文名称（用于双语/英文PDF）</span>
          <input
            type="text"
            className="input-text mt-1 w-full"
            value={form.name_en || ''}
            onChange={(e) => setField('name_en', e.target.value)}
            maxLength={200}
            placeholder="Optional English name"
          />
        </label>

        <label className="block">
          <span className="text-sm text-slate-600">描述</span>
          <textarea
            className="input-text mt-1 w-full"
            rows={2}
            value={form.description || ''}
            onChange={(e) => setField('description', e.target.value)}
            maxLength={1000}
            placeholder="中文描述"
          />
          {fieldErrors.description && <p className="text-xs text-rose-600 mt-1">{fieldErrors.description}</p>}
        </label>

        <label className="block">
          <span className="text-sm text-slate-600">英文描述（用于双语/英文PDF）</span>
          <textarea
            className="input-text mt-1 w-full"
            rows={2}
            value={form.description_en || ''}
            onChange={(e) => setField('description_en', e.target.value)}
            maxLength={1000}
            placeholder="Optional English description"
          />
        </label>

        <div className="grid grid-cols-3 gap-4">
          <label className="block">
            <span className="text-sm text-slate-600">单位</span>
            <input
              type="text"
              className="input-text mt-1 w-full"
              value={form.unit || ''}
              onChange={(e) => setField('unit', e.target.value)}
              placeholder="pcs / kg / m³"
            />
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">默认售价</span>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input-text mt-1 w-full"
              value={form.default_price_rmb}
              onChange={(e) => setField('default_price_rmb', e.target.value)}
            />
            {fieldErrors.default_price_rmb && <p className="text-xs text-rose-600 mt-1">{fieldErrors.default_price_rmb}</p>}
          </label>
          <label className="block">
            <span className="text-sm text-slate-600">货币</span>
            <select
              value={form.currency}
              onChange={(e) => setField('currency', e.target.value)}
              className="input-text mt-1 w-full"
            >
              {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
        </div>

        {!isSales && (
          <label className="block">
            <span className="text-sm text-slate-600">
              默认成本 <span className="text-xs text-slate-400">(仅 boss/finance 可见)</span>
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input-text mt-1 w-60"
              value={form.default_cost_rmb}
              onChange={(e) => setField('default_cost_rmb', e.target.value)}
            />
            {fieldErrors.default_cost_rmb && <p className="text-xs text-rose-600 mt-1">{fieldErrors.default_cost_rmb}</p>}
          </label>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <Link to="/app/products" className="btn-secondary">取消</Link>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? '保存中…' : (isNew ? '创建' : '保存')}
          </button>
        </div>
      </form>
    </div>
  );
}
